import { fetchUser, isSignedInComplete } from "@/api/apis";
import { User } from "@/models/User";

/**
 * auth 로그인 완료 감지 — **팝업(`useAuthPopup`)과 임베드(`useAuthEmbed`)가 공유**한다.
 *
 * 표면(별도 창 / iframe)은 다르지만 "언제 로그인이 끝났는가"를 판단하는 방법은 같다.
 * 두 벌로 두면 **보장 경로인 폴링이 한쪽에서만 고쳐지는** 형태로 썩는다 — 그쪽이 조용히
 * 실패하는 축이라 회귀를 눈치채기도 어렵다.
 *
 * ## 감지는 이중화한다
 * - **빠른 경로**: auth `/popup-complete` 가 보내는 postMessage → 호출처가 `check()` 호출
 * - **보장 경로**: `/api/user` 폴링. 세션은 `.bhaptics.com` 도메인 쿠키라 신호가 유실돼도
 *   여기서 잡힌다. 신호가 아예 안 오는 경로가 실재한다:
 *   · 팝업 + 소셜 로그인 — COOP 로 opener 가 끊긴다
 *   · **임베드 + 소셜 로그인** — breakout 팝업이 opener(=auth iframe)에 홈페이지 오리진을
 *     targetOrigin 으로 보내 브라우저가 버린다
 *
 * 신호를 받아도 **그대로 믿지 않고** 항상 세션을 재조회한다 — 신호는 "지금 확인해봐"
 * 트리거일 뿐이라 위조돼도 최대 효과가 조회 1회다.
 */

/** 폴링 간격. 방치된 창의 요청 수를 억제하려 뒤로 갈수록 늘린다. */
export const POLL_STEPS_MS = [1500, 1500, 3000, 3000, 5000] as const;
export const POLL_STEADY_MS = 8000;
/**
 * 감시 상한. 도달하면 **감시만 멈춘다** — 사용자가 메일에서 OTP 코드를 찾는 동안
 * 입력 중인 화면을 파괴하면 안 된다(감시 중단과 표면 파괴는 다른 일이다).
 */
export const WATCH_TIMEOUT_MS = 5 * 60 * 1000;
/** 판정 조회 상한. hang 하면 in-flight 락이 안 풀려 감시가 멈춘다. */
export const VERIFY_TIMEOUT_MS = 5000;

/** 완료 판정. 타임아웃은 미완료로 보고(실패 아님) 다음 폴에서 다시 시도한다. */
export async function verifySignedIn(): Promise<User | null> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const user = await Promise.race([
      fetchUser(),
      new Promise<null>((resolve) => {
        timer = setTimeout(() => resolve(null), VERIFY_TIMEOUT_MS);
      }),
    ]);
    return isSignedInComplete(user) ? (user as User) : null;
  } finally {
    // 패자 타이머를 정리한다 — 매 폴마다 방치하면 타이머가 쌓인다.
    if (timer) clearTimeout(timer);
  }
}

export interface SessionWatch {
  /** 빠른 경로(신호 수신) 트리거. 폴링과 **직렬화**되어 중복 실행되지 않는다. */
  check: () => void;
  /** 감시 해제. 멱등. */
  stop: () => void;
}

export function startSessionWatch({
  onAuthenticated,
  onTimeout,
}: {
  /** 검증된 세션이 확인됐을 때 **한 번만** 호출된다. */
  onAuthenticated: (user: User) => void;
  /** 상한 도달. 호출처가 대기 UI 를 되돌린다(표면은 건드리지 않는다). */
  onTimeout?: () => void;
}): SessionWatch {
  let cancelled = false;
  let inFlight = false;
  let polls = 0;
  let pollTimer: ReturnType<typeof setTimeout> | undefined;
  // `stop()` 이 참조하므로 **선언이 먼저**여야 한다 — const 로 아래에 두면 TDZ 에 걸릴 수
  // 있는 순서 의존이 생긴다(지금은 우연히 안전할 뿐이다).
  let watchTimeout: ReturnType<typeof setTimeout> | undefined;

  const stop = () => {
    cancelled = true;
    if (pollTimer) clearTimeout(pollTimer);
    if (watchTimeout) clearTimeout(watchTimeout);
  };

  const check = () => {
    void (async () => {
      // `inFlight` 가 없으면 신호가 겹칠 때 두 조회가 모두 통과해 결제가 **두 번** 이어진다
      // (주문 draft 중복). `cancelled` 는 await 이후에도 다시 본다 — 그 사이 사용자가
      // 취소했을 수 있고, 안 걸러내면 "취소했는데 결제가 진행"된다.
      if (cancelled || inFlight) return;
      inFlight = true;
      try {
        const user = await verifySignedIn();
        if (cancelled || !user) return;
        stop();
        onAuthenticated(user);
      } catch (e) {
        // void 호출이라 삼키지 않으면 unhandled rejection 이 된다.
        console.error("Login detection failed.", e);
      } finally {
        inFlight = false;
      }
    })();
  };

  // 다음 폴은 **조회 완료를 기다리지 않고** 고정 간격으로 예약한다. 겹치는 폴은 `check()`
  // 의 in-flight 가드가 건너뛰므로 요청이 쌓이지 않고, 조회가 느릴 때 간격이 밀리지 않는다.
  const scheduleNextPoll = () => {
    if (cancelled) return;
    const delay = POLL_STEPS_MS[polls] ?? POLL_STEADY_MS;
    polls += 1;
    pollTimer = setTimeout(() => {
      check();
      scheduleNextPoll();
    }, delay);
  };

  watchTimeout = setTimeout(() => {
    stop();
    onTimeout?.();
  }, WATCH_TIMEOUT_MS);

  scheduleNextPoll();

  return { check, stop };
}
